import type { SupSingleVaultV3 } from "@/types";
import React, { useMemo } from "react";

import LoaderInPage from "@/components/Loader/InPage";
import LineChart, { type Config } from "@/components/Ui/LineChart";

import { formatCurrencyCompact, formatPercent, roundNumber } from "@/utils";
import subDays from "date-fns/subDays";
import format from "date-fns/format";
import isSameYear from "date-fns/isSameYear";

import { useVaultHistoricalData } from "@/hooks/api/useVaultHistoricalData";

interface VaultDetailsLineChartProps {
  singleVault: SupSingleVaultV3;
}

export default function VaultDetailsLineChart({ singleVault }: VaultDetailsLineChartProps) {
  const params = useMemo(
    () => ({
      startDate: new Date(),
      endDate: subDays(new Date(), 30),
      vault: singleVault,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleVault.id]
  );
  const { data, isLoading, isError } = useVaultHistoricalData(params);

  const { startDate, endDate } = useMemo(() => {
    if (!data || data.length === 0) {
      return { startDate: params.endDate, endDate: params.startDate };
    }
    return {
      startDate: data[data.length - 1].date,
      endDate: data[0].date,
    };
  }, [data, params]);

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex h-[569px] items-center justify-center rounded-bl-[48px] rounded-br-[48px] rounded-tl-[48px] bg-neutral">
        <LoaderInPage />
      </div>
    );
  }

  const config: Config = [
    {
      label: "DATE",
      dataKey: "timestamp",
      stroke: "hsl(var(--twc-neutral-600))",
      xAxis: {
        // @ts-ignore
        tickFormatter: (value: Date) => format(value, "MMM dd").toUpperCase(),
        position: "bottom",
      },
    },
    {
      label: "TVL",
      dataKey: "tvl",
      stroke: "hsl(var(--twc-primary-900))", // @ts-ignore
      yAxis: {
        tickFormatter: value => (value != null ? formatCurrencyCompact(value, 2, 0) : ""),
        formatter: value => (value != null ? roundNumber(value, { maxFractionDigits: 2 }) : undefined),
        position: "left",
        width: 60,
        tickCount: 4,
        startFromZero: true,
      },
      line: {
        dotR: 2,
      },
    },
    {
      label: "Price Per Share (USD)",
      dataKey: "pps_usd",
      stroke: "hsl(var(--twc-primary-600))", // @ts-ignore
      yAxis: {
        tickFormatter: value => (value != null ? formatCurrencyCompact(value, 2, 2) : ""),
        formatter: value => {
          return value != null ? roundNumber(value, { maxFractionDigits: 2 }) : undefined;
        },
        position: "left",
        width: 70,
        tickCount: 4,
      },
      line: {
        dotR: 1,
      },
    },
    {
      label: "APY",
      dataKey: "apy",
      stroke: "hsl(var(--twc-success-800))", // @ts-ignore
      yAxis: {
        tickFormatter: value => (value != null ? formatPercent(value / 100, { maxFractionDigits: 2 }) : ""),
        formatter: value => {
          return value != null ? roundNumber(value, { maxFractionDigits: 2 }) : undefined;
        },
        position: "right",
        width: 60,
        tickCount: 4,
        rangeLimit: { from: -50_000, to: 50_000 },
        startFromZero: true,
      },
      line: {
        dotR: 2,
        strokeDasharray: "4 4",
      },
    },
    {
      label: "Base APY",
      dataKey: "apy_base",
      stroke: "hsl(var(--twc-success-400))", // @ts-ignore
      yAxis: {
        tickFormatter: value => (value != null ? formatPercent(value / 100, { maxFractionDigits: 2 }) : ""),
        formatter: value => {
          return value != null ? roundNumber(value, { maxFractionDigits: 2 }) : undefined;
        },
        position: "right",
        width: 60,
        tickCount: 4,
        rangeLimit: { from: -50_000, to: 50_000 },
        startFromZero: true,
      },
      line: {
        dotR: 1,
        strokeDasharray: "4 8 2",
      },
    },
  ];

  return (
    !isError &&
    data &&
    data.length > 0 && (
      <div className="custom-scrollbar overflow-y-auto rounded-bl-[48px] rounded-br-[48px] rounded-tl-[48px] bg-neutral pb-[20px] pt-[25px] lg:overflow-hidden">
        <h2 className="space-x-[4px] pl-[38px] font-mono text-[16px] font-bold uppercase">
          <span className="font-normal">
            {format(endDate, isSameYear(startDate, endDate) ? "MMM d" : "MMM d yyyy")} - {format(startDate, "MMM d yyyy")}
          </span>
        </h2>
        <div id="chartContainer" className="flex h-[350px] min-w-[900px] items-center justify-center lg:h-[500px]">
          <LineChart data={data} config={config} />
        </div>
      </div>
    )
  );
}
